import React, { FC } from 'react';
import { Box, Text, TextProps } from '@wix/design-system';
import RatingBar from '../RatingBar';
import FeatureTags from '../FeatureTags';
import s from './CardInfo.module.scss';
import { TemplateGalleryCard } from '@common/types';
import { TFunction } from '@wix/fe-essentials-standalone/i18n';

type PriceTextProps = {
  weight: TextProps['weight'];
  getLocalizedPrice: (p: number) => string;
};

export interface CardInfoProps {
  template: TemplateGalleryCard;
  priceTextProps: PriceTextProps;
  isMobileView?: boolean;
  hovered?: boolean;
  t: TFunction;
  language: string;
  showRating?: boolean;
  showFeatures?: boolean;
  className?: string;
  titleProps?: { size: TextProps['size']; weight: TextProps['weight'] };
}

const CardInfo: FC<CardInfoProps> = ({
  isMobileView,
  template,
  hovered,
  t,
  language,
  className = '',
  titleProps = {},
  priceTextProps,
  showFeatures = true,
  showRating = true,
}) => {
  return (
    <>
      <Box
        display="flex"
        align="space-between"
        gap="18px"
        width="100%"
        paddingTop={isMobileView ? '12px' : '0px'}
        className={className}
      >
        <Box minWidth="154px" maxWidth="432px" direction="vertical" gap="2px">
          <Text
            {...titleProps}
            ellipsis
            maxLines={1}
            className={!isMobileView && hovered ? s.hoveredTitle : ''}
          >
            {template.title}
          </Text>
          <PartnerName
            t={t}
            businessName={template.partnerProfileDetails?.businessName}
          />
        </Box>
        <Box display="inline-block" maxWidth="104px">
          <Price
            t={t}
            textProps={priceTextProps}
            templatePrice={template.price!}
          />
        </Box>
      </Box>
      {showRating && (
        <RatingBar template={template} t={t} language={language} />
      )}
      {showFeatures && (
        <FeatureTags features={template.apps} isMobileView={isMobileView} />
      )}
    </>
  );
};

const PartnerName = ({
  businessName,
  t,
}: {
  businessName?: string;
  t: any;
}) => {
  return businessName ? (
    <Text size="tiny" ellipsis maxLines={1} className={s.textColor}>
      {t('marketplace.templates-page.template-card.subtitle')}&nbsp;
      {businessName}
    </Text>
  ) : (
    <Box height="15px"></Box>
  );
};

const Price = ({
  templatePrice,
  textProps,
  t,
}: {
  templatePrice?: number;
  textProps: PriceTextProps;
  t: any;
}) => {
  const isFreeTemplate = templatePrice === 0;

  if (isFreeTemplate) {
    return (
      <Text weight={textProps?.weight}>
        {t('marketplace.templates-page.template-card.free')}
      </Text>
    );
  }

  return (
    <Text
      weight={textProps?.weight}
      ellipsis
      maxLines={1}
      dataHook="card-template-price"
    >
      {textProps?.getLocalizedPrice?.(templatePrice!)}
    </Text>
  );
};

export default CardInfo;
